/**
 * Implements a navigation bar with
 * chain selector and search input
 */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import DropdownInput, { IDropdownInputOption } from './DropdownInput';
import ChainSelector from '../../../components/ChainSelector';
import { IStore } from '../../../reducers';
import useSearchWallets from '../useSearchWalllets';
import { WalletAPI } from '../../../store/createStore';
import { actionSetWatchlistTopWallets } from '../../../actions/wallet_watchlist';
import { networksWithWalletData } from '../../../components/NetworkSelector/core';
import {
  isEVMAddress,
  isElrondAddress,
} from '../../../helpers/checksumAddress';
import Text from '../../../atoms/Text';
import { authTokenSelector } from '../../../selectors/auth';
import { getNetworkFlags } from '../../../wallet/helpers';
import { Network } from '@kattana-io/common';

const SEARCH_LIMIT = 15;
const SEARCH_PLACEHOLDER_TEXT =
  'Paste wallet address or search by nickname to analyze';
const SEARCH_BTN_TEXT = 'Analyze';
const SEARCH_EVM_WIDTH = 622;
const SEARCH_MULTIVERSX_WIDTH = 742;
const SEARCH_TRON_WIDTH = 552;
const SEARCH_TON_WIDTH = 682;

const getSearchWidgetWidth = (chain = '') => {
  const flags = getNetworkFlags(Network.fromChain(chain).chain);
  if (flags.isEVM) {
    return SEARCH_EVM_WIDTH;
  }
  if (flags.isTon) {
    return SEARCH_TON_WIDTH;
  }
  if (flags.isTron) {
    return SEARCH_TRON_WIDTH;
  }
  return SEARCH_MULTIVERSX_WIDTH;
};

const filterDuplicatesByValue = (items: IDropdownInputOption[]) => {
  const uniqueWallets = items
    .reduce((cons, item) => cons.set(item.value, item), new Map())
    .values();
  return Array.from(uniqueWallets);
};

interface ISearchProps {
  chain?: string;
  address?: string;
}

const Search = ({ chain, address }: ISearchProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authToken = useSelector(authTokenSelector);

  const [search, setSearch] = useState('');
  const [defOptions, setDefOptions] = useState<IDropdownInputOption[]>([]);

  const { searchLoading, searchedData } = useSearchWallets(
    search,
    SEARCH_LIMIT,
    chain
  );

  const topWallets = useSelector(
    (state: IStore) => state.wallet_watchlist.topWallets
  );

  useEffect(() => {
    if (topWallets.length) {
      setDefOptions(
        topWallets.map((wallet) => ({
          label: wallet.address,
          subLabel: wallet.nickname,
          value: wallet.address,
        }))
      );
    } else {
      WalletAPI.fetchTopWallets(authToken).then((topWallets) => {
        if (topWallets?.length) {
          setDefOptions(
            topWallets.map((wallet) => ({
              label: wallet.address,
              subLabel: wallet.nickname,
              value: wallet.address,
            }))
          );
          dispatch(actionSetWatchlistTopWallets(topWallets));
        }
      });
    }
  }, []);

  const onChainSelect = (value: string) => {
    navigate(`/en/wallet-overview/${value}/${address}`);
  };

  const onSelectWallet = (value: string) => {
    navigate(`/en/wallet-overview/${chain}/${value}`);
  };

  const onAnalyze = () => {
    navigate(`/en/wallet-overview/${chain}/${search}`);
  };

  const options: IDropdownInputOption[] =
    searchedData?.map((wallet) => ({
      label: wallet.address,
      subLabel: wallet.nickname,
      value: wallet.address,
    })) || [];
  return (
    <div className='overview__header'>
      <ChainSelector
        selected={chain || ''}
        onSet={onChainSelect}
        networks={networksWithWalletData}
      />
      <DropdownInput
        value={search}
        setValue={setSearch}
        options={filterDuplicatesByValue(options)}
        onSelect={onSelectWallet}
        loading={searchLoading}
        placeholder={SEARCH_PLACEHOLDER_TEXT}
        defaultOptions={filterDuplicatesByValue(defOptions)}
        btnText={SEARCH_BTN_TEXT}
        onBtnClick={onAnalyze}
        btnDisabled={!isEVMAddress(search) && !isElrondAddress(search)}
        gap={8}
        width={`calc(100% - ${getSearchWidgetWidth(chain)}px)`}
      />
      <Link to='/' className='overview__header__go_back'>
        <div className='overview__header__go_back__icon icon-arrow-left' />
        <Text size={12}>Go to terminal</Text>
      </Link>
    </div>
  );
};

export default Search;
